import { PageHeader, LoadingState, DataTable } from '../components/AdminPageUI';
import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Container, Grid, Card, CardContent, TextField, MenuItem, Paper, Table, TableBody, TableCell, TableHead, TableRow, Avatar } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../utils/Api';

export default function TransportAssignmentPage() {
  const [loading, setLoading] = useState(true);
  const [routes, setRoutes] = useState([]);
  const [terms, setTerms] = useState([]);
  const [assignments, setAssignments] = useState([]);
  
  // Selection State
  const [selectedTermId, setSelectedTermId] = useState('');
  
  // Form State
  const [selectedRouteId, setSelectedRouteId] = useState('');
  const [teacherSearch, setTeacherSearch] = useState('');
  const [teacherSearchResults, setTeacherSearchResults] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const userStr = localStorage.getItem('user');
      let schoolId = '';
      if (userStr) {
        const user = JSON.parse(userStr);
        schoolId = user.school?._id || user.school || '';
      }

      const [routeRes, termRes] = await Promise.all([
        api.get('/transport/routes'),
        api.get('/term/all', { params: { school: schoolId } })
      ]);
      
      const fetchedTerms = termRes.data.allTerms || [];
      setRoutes(routeRes.data.routes || []);
      setTerms(fetchedTerms);
      if (fetchedTerms.length > 0) {
        setSelectedTermId(fetchedTerms[0]._id);
      }
    } catch (err) {
      toast.error('Failed to load initial data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignments = async () => {
    try {
      const res = await api.get('/transport/assignments');
      setAssignments(res.data.assignments || []);
    } catch (err) {
      toast.error('Failed to fetch assignments');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedTermId) {
      fetchAssignments();
    }
  }, [selectedTermId]);

  const handleSearchTeacher = async () => {
    if (!teacherSearch.trim()) return;
    try {
      const res = await api.get('/teachers');
      const teachers = res.data.teachers || res.data || [];
      const filtered = teachers.filter(t => 
        (t.user?.name || t.name || '').toLowerCase().includes(teacherSearch.toLowerCase())
      ).slice(0, 5);
      setTeacherSearchResults(filtered);
    } catch (err) {
      toast.error('Failed to search teachers');
    }
  };

  const handleAssign = async () => {
    if (!selectedTeacher || !selectedTermId || !selectedRouteId) {
      toast.warning('Please complete all assignment fields.');
      return;
    }

    try {
      setIsSubmitting(true);
      // TransportAssignment.teacher is ref: 'User', must send the User's _id
      const teacherUserId = selectedTeacher.user?._id || selectedTeacher._id;
      
      await api.post('/transport/assignments', {
        teacherId: teacherUserId,
        routeId: selectedRouteId,
        termId: selectedTermId
      });
      
      toast.success('Teacher assigned successfully!');
      
      // Reset form
      setSelectedTeacher(null);
      setTeacherSearchResults([]);
      setTeacherSearch('');
      setSelectedRouteId('');
      
      // Refresh table
      fetchAssignments();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign teacher');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <LoadingState />
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <PageHeader title={<>Bus Assignments (Teachers)</>} description={<>Assign teachers to buses and manage their transport duties.</>}></PageHeader>

      <Grid container spacing={3}>
        {/* Left Side: Assignment Form */}
        <Grid size={{ xs: 12, md: 4 }}   >
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>Assign Teacher to Route</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>

                <TextField
                  select
                  size="small"
                  label="Academic Term"
                  value={selectedTermId}
                  onChange={(e) => setSelectedTermId(e.target.value)}
                >
                  {terms.map(t => (
                    <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  size="small"
                  label="Transport Route"
                  value={selectedRouteId}
                  onChange={(e) => setSelectedRouteId(e.target.value)}
                >
                  {routes.map(r => (
                    <MenuItem key={r._id} value={r._id}>{r.name}</MenuItem>
                  ))}
                </TextField>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Search Teacher Name"
                    value={teacherSearch}
                    onChange={(e) => setTeacherSearch(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearchTeacher()}
                  />
                  <Button variant="outlined" onClick={handleSearchTeacher} sx={{ minWidth: '40px', p: 0 }}>
                    <SearchIcon />
                  </Button>
                </Box>

                {teacherSearchResults.length > 0 && !selectedTeacher && (
                  <Paper className="workspace-section" variant="outlined" sx={{ p: 1, maxHeight: 150, overflowY: 'auto' }}>
                    {teacherSearchResults.map(teacher => (
                      <Box 
                        key={teacher._id} 
                        onClick={() => setSelectedTeacher(teacher)}
                        sx={{ p: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Avatar src={teacher.user?.profilePicture || teacher.profilePicture} sx={{ width: 24, height: 24 }} />
                        <Typography variant="body2">{teacher.user?.name || teacher.name}</Typography>
                      </Box>
                    ))}
                  </Paper>
                )}

                {selectedTeacher && (
                  <Paper className="workspace-section" variant="outlined" sx={{ p: 2, bgcolor: 'primary.50', borderColor: 'primary.main', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                      <Typography variant="subtitle2" color="primary.main">Selected Teacher:</Typography>
                      <Typography variant="body1" fontWeight="bold">{selectedTeacher.user?.name || selectedTeacher.name}</Typography>
                    </Box>
                    <Button size="small" color="error" onClick={() => setSelectedTeacher(null)}>Clear</Button>
                  </Paper>
                )}

                <Button 
                  variant="contained" 
                  size="large"
                  disabled={isSubmitting || !selectedTeacher || !selectedRouteId}
                  onClick={handleAssign}
                  sx={{ mt: 2 }}
                >
                  {isSubmitting ? 'Assigning...' : 'Confirm Assignment'}
                </Button>

              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Side: Assignments Table */}
        <Grid size={{ xs: 12, md: 8 }}   >
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Current Teacher Assignments
              </Typography>

              <DataTable component={Paper} variant="outlined" sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: 'background.default' }}>
                    <TableRow>
                      <TableCell>Route</TableCell>
                      <TableCell>Assigned Teacher</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {assignments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} align="center" sx={{ py: 3 }}>
                          <Typography color="text.secondary">No teachers assigned yet.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      assignments.map((assignment) => (
                        <TableRow key={assignment._id} hover>
                          <TableCell sx={{ fontWeight: 'bold' }}>{assignment.route?.name}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 24, height: 24 }} src={assignment.teacher?.user?.profilePicture || assignment.teacher?.profilePicture} />
                              <Typography variant="body2">{assignment.teacher?.user?.name || assignment.teacher?.name || 'Unknown'}</Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </DataTable>

            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
